import { NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Bell,
  BookOpenText,
  Building2,
  CalendarClock,
  ClipboardCheck,
  ClipboardList,
  Compass,
  FileText,
  Heart,
  Home,
  LayoutList,
  Settings as SettingsIcon,
  ShieldAlert,
  UserCircle,
  Users,
  Users2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

// role gating helpers
const roleLevel = { volunteer: 0, employee: 1, manager: 2, campus_pastor: 2, po_admin: 3, super_admin: 4 };

const STAFF_NAV = [
  { to: "/", label: "Dashboard", icon: Home, minRole: "employee", testId: "nav-dashboard" },
  { to: "/team", label: "My Team", icon: Users2, minRole: "employee", testId: "nav-my-team", conditional: "show_my_team" },
  { to: "/lift", label: "LIFT", icon: BookOpenText, minRole: "employee", testId: "nav-lift", conditional: "show_lift_nav" },
  { to: "/compass", label: "COMPASS", icon: Compass, minRole: "employee", testId: "nav-compass", conditional: "show_compass_nav" },
  { to: "/rhythm", label: "RHYTHM", icon: CalendarClock, minRole: "employee", testId: "nav-rhythm" },
  { to: "/rhythm/weekly-wins", label: "Weekly Wins", icon: ClipboardCheck, minRole: "employee", testId: "nav-weekly-wins", conditional: "show_weekly_wins_submit" },
  { to: "/rhythm/weekly-wins/rollup", label: "Weekly Wins Roll-Up", icon: LayoutList, minRole: "employee", testId: "nav-weekly-wins-rollup", conditional: "show_weekly_wins_rollup" },
  { to: "/er", label: "ER Center", icon: Heart, minRole: "employee", testId: "nav-er" },
  { to: "/training", label: "Training", icon: FileText, minRole: "employee", testId: "nav-training" },
  { to: "/resources", label: "Resources", icon: BookOpenText, minRole: "employee", testId: "nav-resources" },
  { to: "/directory", label: "Directory", icon: Users, minRole: "employee", testId: "nav-directory" },
  { to: "/faith-journey", label: "My Faith Journey", icon: Heart, minRole: "employee", testId: "nav-faith-journey" },
  { to: "/faith-journeys", label: "Faith Journeys", icon: Heart, minRole: "po_admin", testId: "nav-faith-journeys-roster" },
  { to: "/praise-report", label: "Praise Report", icon: Heart, minRole: "po_admin", testId: "nav-praise-report" },
  { to: "/profile", label: "My Profile", icon: UserCircle, minRole: "employee", testId: "nav-profile" },
  { to: "/notifications", label: "Notifications", icon: Bell, minRole: "employee", testId: "nav-notifications" },
  { to: "/lift/cycles", label: "LIFT Cycles", icon: ClipboardList, minRole: "po_admin", testId: "nav-lift-cycles" },
  { to: "/lift/redaction-queue", label: "LIFT Redaction", icon: ShieldAlert, minRole: "po_admin", testId: "nav-lift-redaction" },
  { to: "/compass/po-queue", label: "COMPASS Queue", icon: ClipboardList, minRole: "po_admin", testId: "nav-compass-queue" },
  { to: "/compass/calibration", label: "COMPASS Calibration", icon: Compass, minRole: "po_admin", testId: "nav-compass-calibration" },
  { to: "/rhythm/dashboard", label: "RHYTHM Dashboard", icon: CalendarClock, minRole: "po_admin", testId: "nav-rhythm-dashboard" },
  { to: "/rhythm/admin", label: "RHYTHM Admin", icon: CalendarClock, minRole: "po_admin", testId: "nav-rhythm-admin" },
  { to: "/er/cases", label: "ER Cases", icon: Heart, minRole: "po_admin", testId: "nav-er-cases" },
  { to: "/er/resources/admin", label: "ER Resources", icon: FileText, minRole: "po_admin", testId: "nav-er-resources" },
  { to: "/training/team", label: "Training Compliance (Team)", icon: FileText, minRole: "manager", testId: "nav-training-team" },
  { to: "/training/admin", label: "Training Content", icon: FileText, minRole: "po_admin", testId: "nav-training-admin" },
  { to: "/training/reports", label: "Training Reports", icon: ClipboardList, minRole: "po_admin", testId: "nav-training-reports" },
  { to: "/resources/admin", label: "Resources Admin", icon: BookOpenText, minRole: "po_admin", testId: "nav-resources-admin" },
  { to: "/volunteer-invites", label: "Volunteer Invites", icon: UserCircle, minRole: "po_admin", testId: "nav-volunteer-invites" },
  { to: "/week-ahead", label: "Week Ahead Digest", icon: Heart, minRole: "po_admin", testId: "nav-week-ahead" },
  { to: "/people/manage", label: "People Management", icon: ClipboardList, minRole: "po_admin", testId: "nav-users" },
  { to: "/campuses", label: "Campuses", icon: Building2, minRole: "super_admin", testId: "nav-campuses" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, minRole: "super_admin", testId: "nav-settings" },
  { to: "/compass/settings", label: "COMPASS Content", icon: Compass, minRole: "super_admin", testId: "nav-compass-settings" },
  { to: "/er/notice/edit", label: "ER Notice", icon: Heart, minRole: "super_admin", testId: "nav-er-notice" },
  { to: "/audit-log", label: "Audit Log", icon: ShieldAlert, minRole: "super_admin", testId: "nav-audit" },
];

const VOLUNTEER_NAV = [
  { to: "/volunteer", label: "Dashboard", icon: Home, testId: "nav-vol-dashboard" },
  { to: "/faith-journey", label: "My Faith Journey", icon: Heart, testId: "nav-vol-faith-journey" },
  { to: "/training", label: "My Trainings", icon: FileText, testId: "nav-vol-training" },
  { to: "/resources", label: "Resources", icon: BookOpenText, testId: "nav-vol-resources" },
  { to: "/notifications", label: "Notifications", icon: Bell, testId: "nav-vol-notifications" },
  { to: "/profile", label: "My Profile", icon: UserCircle, testId: "nav-vol-profile" },
];

export function useNavItems() {
  const { user } = useAuth();
  const [hints, setHints] = useState(null);
  useEffect(() => {
    if (!user || user.role === "volunteer") return;
    api.get("/nav-hints").then((r) => setHints(r.data)).catch(() => setHints({}));
  }, [user]);
  if (user?.role === "volunteer") return VOLUNTEER_NAV;
  const level = roleLevel[user?.role || "employee"] || 1;
  const isPO = ["po_admin", "super_admin"].includes(user?.role);
  return STAFF_NAV.filter((n) => {
    if (level < roleLevel[n.minRole]) return false;
    // Conditional visibility (nav-hints). PO/Super Admin see the ones marked
    // as PO-visible regardless; the conditional flag only prunes the surface
    // for non-PO users.
    if (n.conditional && !isPO) {
      if (!hints) return false; // still loading — safer to hide until we know
      return !!hints[n.conditional];
    }
    return true;
  });
}

export function Sidebar({ onNavigate }) {
  const items = useNavItems();
  return (
    <aside className="hidden lg:flex flex-col w-64 shrink-0 bg-cream-100 border-r border-sage-300/70 min-h-[calc(100vh-72px)] p-5 gap-1">
      <nav className="flex flex-col gap-1 mt-2">
        {items.map(({ to, label, icon: Icon, testId }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            data-testid={testId}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                isActive
                  ? "bg-forest-600 text-cream-100"
                  : "text-charcoal hover:bg-sage-200/70"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon
                  className={`h-4 w-4 ${isActive ? "text-cream-100" : "text-forest-600"}`}
                  strokeWidth={1.5}
                />
                <span>{label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto rounded-2xl bg-forest-600 text-cream-100 p-4 shadow-card">
        <p className="font-serif text-xl leading-tight">Rooted, Together.</p>
        <p className="mt-2 text-xs text-cream-100/80 leading-relaxed">
          MyPatria is our People Operations home. Phase 1 of 5 rolling out to your campus.
        </p>
      </div>
    </aside>
  );
}

/** Icons re-exported for other pages that map module keys to icons. */
export const MODULE_ICONS = { BookOpenText, Compass, Heart, FileText };
